import { FC, useEffect, useRef, useState } from 'react';
import { Input } from 'antd';
import { useParams } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';
import {
  useProcInstanceContext,
  useUserProcStepContext,
} from '@/entities/proc';
import { useProcComponentFill } from '@/shared/api';
import { useThemeContext, themeConfig } from '@/shared/theme';
import { UserInstanceParams } from '@/shared/types';
import { Col } from '@/shared/ui';

import { captureError } from '@/shared/utils/error-reporting';

type Props = {
  id: string;
  value?: string;
  label?: string;
  placeholder?: string;
};

export const InputInputMode: FC<Props> = ({
  id,
  value,
  label = '',
  placeholder = '',
  ...rest
}) => {
  const { theme } = useThemeContext();

  const [val, setVal] = useState(value);

  const { code } = useParams<UserInstanceParams>();
  const { procInstance } = useProcInstanceContext();
  const { stepId } = useUserProcStepContext();
  const debouncedValue = useDebounce(val);
  const isTouched = useRef(false);

  const { mutate: updateProcComponent } = useProcComponentFill();

  const handleSubmit = () => {
    if (isTouched.current) {
      try {
        updateProcComponent(
          {
            pathParams: { instanceId: procInstance?.id!, stepId: stepId! },
            queryParams: { code },
            body: {
              ...rest,
              label,
              placeholder,
              id,
              value: debouncedValue,
            },
          },
          {
            onSuccess: () => {
              isTouched.current = true;
            },
          },
        );
      } catch (error) {
        captureError(error, {
          module: 'input_input_mode',
          action: 'update_proc_component',
        });
      }
    }
  };

  useEffect(() => {
    handleSubmit();
  }, [debouncedValue]);

  useEffect(() => {
    setVal(value);
  }, [value]);

  return (
    <Col dataTestId="input-component" alignItems="flex-start">
      {Boolean(label) && <label style={{ display: 'block' }}>{label}</label>}
      <Input
        value={val}
        placeholder={placeholder}
        style={{
          marginTop: 8,
          width: 300,
          maxWidth: '100%',
          color: themeConfig[theme].colors.text,
        }}
        onChange={(e) => {
          isTouched.current = true;
          setVal(e.target.value);
        }}
      />
    </Col>
  );
};
